import {
  ArrowRightIcon,
  ChevronLeftIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/20/solid';
import { useEffect } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import useMediaQuery from 'src/shared/hooks/useMediaQuery';

import { cn } from 'src/shared/components/ui';
import { ButtonV2, Textarea } from 'src/shared/components/ui';

interface InlineCaptionOverlayProps<
  T extends { id: string; caption?: string }
> {
  isOpen: boolean;
  onClose: () => void;
  items: T[];
  currentIndex: number;
  captionValue: string;
  onCaptionChange: (value: string) => void;
  isEditMode: boolean;
  onSave: () => Promise<void> | void;
  onNext: () => Promise<void> | void;
  onPrevious: () => Promise<void> | void;
  onDone: () => Promise<void> | void;
  renderPreview: (item: T, previewUrl: string | undefined) => React.ReactNode;
  getPreviewUrl: (item: T) => string | undefined;
  placeholder?: string;
  /** When true, the preview takes more vertical space (for non-image files like PDFs) */
  isFullScreen?: boolean;
  /** Optional content rendered below the caption input (e.g. a warning for the current item). */
  warning?: React.ReactNode;
}

/**
 * Caption overlay that renders inline within a parent container (e.g. the right panel
 * of AddReferencesModal). Unlike CaptionModal which uses fixed positioning to cover the
 * viewport, this component uses absolute positioning to overlay only its parent.
 *
 * The parent element MUST have `position: relative` for this to work correctly.
 */
export function InlineCaptionOverlay<
  T extends { id: string; caption?: string }
>({
  isOpen,
  onClose,
  items,
  currentIndex,
  captionValue,
  onCaptionChange,
  isEditMode,
  onSave,
  onNext,
  onPrevious,
  onDone,
  renderPreview,
  getPreviewUrl,
  placeholder = 'What do you like about this image?',
  isFullScreen = false,
  warning
}: InlineCaptionOverlayProps<T>) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const currentItem = items[currentIndex];
  const previewUrl = currentItem ? getPreviewUrl(currentItem) : undefined;

  const handleBackdropClick = async (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      await onSave();
      onClose();
    }
  };

  const handleCloseClick = async () => {
    await onSave();
    onClose();
  };

  // Handle ESC key to close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseClick();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!currentItem) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeIn', delay: 0.1 }}
          className={cn(
            'fixed md:absolute inset-0 z-50 flex items-center justify-center',
            isFullScreen ? '!fixed' : ''
          )}
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{
              duration: 0.25,
              ease: [0.4, 0, 0.2, 1]
            }}
            className="backdrop-blur-md w-full h-full p-4 md:p-6 flex flex-col items-center justify-center gap-3 md:gap-4 overflow-y-auto"
            style={{
              background: isMobile
                ? '#FFFFFF'
                : 'radial-gradient(ellipse at center, rgba(249, 250, 251, 0) 0%, rgba(255, 255, 255, 0.85) 85%, rgba(255, 255, 255, 0.95) 100%)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={handleCloseClick}
              className="absolute top-4 right-4 p-2 hover:bg-white/30 rounded-full transition-colors z-10 mobile:bg-[#F9FAFB66]/30 mobile:backdrop-blur-md mobile:border mobile:top-2 mobile:right-2"
              aria-label="Close"
            >
              <XMarkIcon className="size-6 text-gray-700" />
            </button>

            {/* Preview Display */}
            <div
              className={`relative inline-flex items-center justify-center ${
                isFullScreen
                  ? 'flex-1 min-h-0 w-full max-w-[90%] md:h-[calc(100%-200px)] md:flex-none'
                  : 'md:h-[280px]'
              }`}
            >
              <AnimatePresence mode="wait">
                {previewUrl && (
                  <motion.div
                    key={`${currentItem.id}-${currentIndex}`}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{
                      duration: 0.5,
                      delay: 0.2,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                    className={`relative h-full flex items-center justify-center [&>img]:h-full [&>img]:w-auto [&>img]:max-w-full [&>img]:object-contain ${
                      isFullScreen ? 'w-full' : 'w-auto'
                    }`}
                  >
                    {renderPreview(currentItem, previewUrl)}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Caption Input */}
            <div className="w-full max-w-sm md:max-w-md flex flex-col gap-2">
              <Textarea
                classNames={{
                  input: '!bg-gray-1',
                  inputWrapper:
                    '!bg-gray-1 focus-within:!bg-gray-1 focus:!bg-gray-1 hover:!bg-gray-1 !border-gray-4 rounded-xl',
                  mainWrapper: '!bg-gray-1',
                  innerWrapper: 'items-end'
                }}
                value={captionValue}
                onChange={(e) => onCaptionChange(e.target.value)}
                placeholder={placeholder}
                rows={isMobile ? 2 : 3}
                autoFocus
              />
              {warning}
            </div>

            {/* Navigation Buttons */}
            {isEditMode || items.length === 1 ? (
              <div className="flex justify-between w-full max-w-sm md:max-w-md px-2 md:px-0">
                <ButtonV2 color="tertiary" onPress={handleCloseClick}>
                  Cancel
                </ButtonV2>
                <ButtonV2
                  color="primary"
                  onPress={async () => {
                    await onSave();
                    onDone();
                  }}
                  endContent={<PlusIcon className="size-4" />}
                >
                  Add
                </ButtonV2>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full max-w-sm md:max-w-md px-2 md:px-0">
                <ButtonV2
                  color="fourth"
                  onPress={onPrevious}
                  isDisabled={currentIndex === 0}
                  startContent={<ChevronLeftIcon className="size-4" />}
                >
                  Previous
                </ButtonV2>

                {items.length > 1 && (
                  <div className="w-full flex flex-col gap-2">
                    <div className="flex gap-2 items-center justify-center">
                      {items.map((_, index) => (
                        <div
                          key={index}
                          className={`h-1.5 rounded-full transition-all ${
                            index === currentIndex
                              ? 'bg-primary w-8'
                              : 'bg-gray-4 w-1.5'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {currentIndex < items.length - 1 ? (
                  <ButtonV2
                    color="primary"
                    onPress={onNext}
                    endContent={<ArrowRightIcon className="size-4" />}
                  >
                    Add
                  </ButtonV2>
                ) : (
                  <ButtonV2
                    color="primary"
                    onPress={async () => {
                      await onNext();
                      onDone();
                    }}
                    endContent={<PlusIcon className="size-4" />}
                  >
                    Add
                  </ButtonV2>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
